namespace CustomerDexImaging.Models

open System

type QueueName =
  | ServiceRequest
  | ServiceEtaRequest
  | ServiceFormRequest
  | SupplyRequest
  | SupplyFormRequest
  | MeterReadingRequest
  | MeterReadingFormRequest
  | PasswordResetRequest

  member this.toString =
    match this with
    | ServiceRequest -> "servicerequest"
    | ServiceEtaRequest -> "serviceetarequest"
    | ServiceFormRequest -> "serviceformrequest"
    | SupplyRequest -> "supplyrequest"
    | SupplyFormRequest -> "supplyformrequest"
    | MeterReadingRequest -> "meterreadingrequest"
    | MeterReadingFormRequest -> "meterreadingformrequest"
    | PasswordResetRequest -> "passwordresetrequest"

type Contact = { Id: int; Email: string }

type AddContactRequest = {
  FirstName: string
  MiddleName: string
  LastName: string
  Phone: string
  Email: string
}

type PasswordResetRequestMessage = {
  ContactId: int
  Email: string
}

type ContactSelectionInfo =
  | Contact of Contact
  | NewContact of AddContactRequest

  member this.Email =
    match this with
    | ContactSelectionInfo.Contact c -> c.Email
    | ContactSelectionInfo.NewContact c -> c.Email

  member this.Greeting =
    match this with
    | ContactSelectionInfo.Contact c -> "Hi,"
    | ContactSelectionInfo.NewContact c -> sprintf "Hi %s %s," c.FirstName c.LastName

type NewMeterReadingRequest = {
  MeterId: int
  MeterType: string
  Reading: int
}

type NewMeterReadingRequestsMessage = {
  EquipmentId: int
  EquipmentNumber: string
  Contact: ContactSelectionInfo
  SendConfirmationEmail: bool
  Readings: NewMeterReadingRequest list
}

type SupplyCategory =
  | Black
  | Cyan
  | Magenta
  | Yellow
  | BlackInk
  | PhotoBlackInk
  | MatteBlackInk
  | LightBlackInk
  | CyanInk
  | LightCyanInk
  | PhotoCyanInk
  | PhotoLightCyanInk
  | MagentaInk
  | LightMagentaInk
  | PhotoMagentaInk
  | PhotoLightMagentaInk
  | VividMagentaInk
  | VividLightMagentaInk
  | YellowInk
  | PhotoYellowInk
  | GreyInk
  | LightGreyInk
  | PhotoGreyInk
  | WhiteInk
  | OptimizerInk
  | RedInk
  | ChromaticRedInk
  | BlueInk
  | ChromaticBlueInk
  | GreenInk
  | ChromaticGreenInk
  | MultiColorInk
  | FluorescentRedInk
  | PhotoLightGreyInk
  | OvercoatInk
  | OrangeInk
  | BrownInk
  | ReflexBlueInk
  | GoldInk
  | PurpleInk
  | GlossEnhancerInk
  | Drum
  | WasteHopper
  | Staples

  member this.toString =
    match this with
    | Black -> "Black Toner"
    | Cyan -> "Cyan Toner"
    | Magenta -> "Magenta Toner"
    | Yellow -> "Yellow Toner"
    | BlackInk -> "Black Ink"
    | PhotoBlackInk -> "Photo Black Ink"
    | MatteBlackInk -> "Matte Black Ink"
    | LightBlackInk -> "Light Black Ink"
    | CyanInk -> "Cyan Ink"
    | LightCyanInk -> "Light Cyan Ink"
    | PhotoCyanInk -> "Photo Cyan Ink"
    | PhotoLightCyanInk -> "Photo Light Cyan Ink"
    | MagentaInk -> "Magenta Ink"
    | LightMagentaInk -> "Light Magenta Ink"
    | PhotoMagentaInk -> "Photo Magenta Ink"
    | PhotoLightMagentaInk -> "Photo Light Magenta Ink"
    | VividMagentaInk -> "Vivid Magenta Ink"
    | VividLightMagentaInk -> "Vivid Light Magenta Ink"
    | YellowInk -> "Yellow Ink"
    | PhotoYellowInk -> "Photo Yellow Ink"
    | GreyInk -> "Grey Ink"
    | LightGreyInk -> "Light Grey Ink"
    | PhotoGreyInk -> "Photo Grey Ink"
    | WhiteInk -> "White Ink"
    | OptimizerInk -> "Optimizer Ink"
    | RedInk -> "Red Ink"
    | ChromaticRedInk -> "Chromatic Red Ink"
    | BlueInk -> "Blue Ink"
    | ChromaticBlueInk -> "Chromatic Blue Ink"
    | GreenInk -> "Green Ink"
    | ChromaticGreenInk -> "Chromatic Green Ink"
    | MultiColorInk -> "Multi Color Ink"
    | FluorescentRedInk -> "Fluorescent Red Ink"
    | PhotoLightGreyInk -> "Photo Light Grey Ink"
    | OvercoatInk -> "Overcoat Ink"
    | OrangeInk -> "Orange Ink"
    | BrownInk -> "Brown Ink"
    | ReflexBlueInk -> "Reflex Blue Ink"
    | GoldInk -> "Gold Ink"
    | PurpleInk -> "Purple Ink"
    | GlossEnhancerInk -> "Gloss Enhancer Ink"
    | Drum -> "Drum/Imaging Unit"
    | WasteHopper -> "Waste Hopper"
    | Staples -> "Staples"

  member this.toSort =
    match this with
    | Black -> 1
    | BlackInk -> 2
    | BlueInk -> 3
    | BrownInk -> 4
    | ChromaticBlueInk -> 5
    | ChromaticGreenInk -> 6
    | ChromaticRedInk -> 7
    | Cyan -> 8
    | CyanInk -> 9
    | Drum -> 51
    | FluorescentRedInk -> 11
    | GlossEnhancerInk -> 12
    | GoldInk -> 13
    | GreenInk -> 14
    | GreyInk -> 15
    | LightBlackInk -> 16
    | LightCyanInk -> 17
    | LightGreyInk -> 18
    | LightMagentaInk -> 19
    | Magenta -> 20
    | MagentaInk -> 21
    | MatteBlackInk -> 22
    | MultiColorInk -> 23
    | OptimizerInk -> 24
    | OrangeInk -> 25
    | OvercoatInk -> 26
    | PhotoBlackInk -> 27
    | PhotoCyanInk -> 28
    | PhotoGreyInk -> 29
    | PhotoLightCyanInk -> 30
    | PhotoLightGreyInk -> 31
    | PhotoLightMagentaInk -> 32
    | PhotoMagentaInk -> 33
    | PhotoYellowInk -> 34
    | PurpleInk -> 35
    | RedInk -> 36
    | ReflexBlueInk -> 37
    | Staples -> 52
    | VividLightMagentaInk -> 39
    | VividMagentaInk -> 40
    | WasteHopper -> 53
    | WhiteInk -> 42
    | Yellow -> 43
    | YellowInk -> 44

  static member getCases() = [
    SupplyCategory.Black
    SupplyCategory.Cyan
    SupplyCategory.Magenta
    SupplyCategory.Yellow
    SupplyCategory.BlackInk
    SupplyCategory.PhotoBlackInk
    SupplyCategory.MatteBlackInk
    SupplyCategory.LightBlackInk
    SupplyCategory.CyanInk
    SupplyCategory.LightCyanInk
    SupplyCategory.PhotoCyanInk
    SupplyCategory.PhotoLightCyanInk
    SupplyCategory.MagentaInk
    SupplyCategory.LightMagentaInk
    SupplyCategory.PhotoMagentaInk
    SupplyCategory.PhotoLightMagentaInk
    SupplyCategory.VividMagentaInk
    SupplyCategory.VividLightMagentaInk
    SupplyCategory.YellowInk
    SupplyCategory.PhotoYellowInk
    SupplyCategory.GreyInk
    SupplyCategory.LightGreyInk
    SupplyCategory.PhotoGreyInk
    SupplyCategory.WhiteInk
    SupplyCategory.OptimizerInk
    SupplyCategory.RedInk
    SupplyCategory.ChromaticRedInk
    SupplyCategory.BlueInk
    SupplyCategory.ChromaticBlueInk
    SupplyCategory.GreenInk
    SupplyCategory.ChromaticGreenInk
    SupplyCategory.MultiColorInk
    SupplyCategory.FluorescentRedInk
    SupplyCategory.PhotoLightGreyInk
    SupplyCategory.OvercoatInk
    SupplyCategory.OrangeInk
    SupplyCategory.BrownInk
    SupplyCategory.ReflexBlueInk
    SupplyCategory.GoldInk
    SupplyCategory.PurpleInk
    SupplyCategory.GlossEnhancerInk
    SupplyCategory.Drum
    SupplyCategory.WasteHopper
    SupplyCategory.Staples
  ]

type MpsScan = {
  MpsSystem : string
  Date: DateTime option
  Black: int option
  Cyan: int option
  Yellow: int option
  Magenta: int option
  MonoReading: int64 option
  ColorReading: int64 option
}

type SupplyRequestMessage = {
  EquipmentId: int
  EquipmentNumber: string
  Contact: ContactSelectionInfo
  SupplyCategories: SupplyCategory list
  MpsScan: MpsScan option
  PurchaseOrderNumber : string option
}

type ServiceCallAssignment =
  | ToHelpDesk
  | ToTechnician

type ServiceCallReason =
  | ErrorCode
  | WillNotPrint
  | WillNotScan
  | CopyQuality
  | MechanicalFailure
  | Noise
  | PaperJam
  | Other

  member this.friendlyDescription =
    match this with
    | ErrorCode -> "Error Code – Please provide the error code"
    | WillNotPrint -> "Unable to Print or Install Print Driver"
    | WillNotScan -> "Unable to Scan to Computer or Email"
    | CopyQuality -> "Copy Quality"
    | MechanicalFailure -> "Hardware Issue"
    | Noise -> "Noise"
    | PaperJam -> "Paper Jam"
    | Other -> "Other - Please provide detailed description"

  member this.defaultCallAssignment =
    match this with
    | ErrorCode -> ToHelpDesk
    | WillNotPrint -> ToHelpDesk
    | WillNotScan -> ToHelpDesk
    | CopyQuality -> ToTechnician
    | MechanicalFailure -> ToTechnician
    | Noise -> ToTechnician
    | PaperJam -> ToTechnician
    | Other -> ToTechnician

  static member getCases = [
    ErrorCode
    WillNotPrint
    WillNotScan
    CopyQuality
    MechanicalFailure
    Noise
    PaperJam
    Other
  ]

type ServiceRequestMessage = {
  EquipmentId: int
  EquipmentNumber: string
  Contact: ContactSelectionInfo
  ServiceCallReason: ServiceCallReason
  Description: string
  PurchaseOrderNumber : string option
}

type ServiceCallEtaRequestMessage = {
  CallId: int
  CallNumber: string
  EquipmentNumber: string
  CustomerContactName: string option
  CustomerContactEmail: string
}

type SupplyItemFormRequest = {
  EquipmentId: string
  MonoMeter: int option
  ColorMeter: int option
  Make: string
  Model: string
  SuppliesNeeded: string
}

type SupplyFormRequest = {
  RecaptchaResponse: string
  FirstAndLastName: string
  CompanyName: string
  ShippingAddress: string
  EquipmentLocation: string option
  EmailAddress: string
  BusinessPhone: string
  Items: SupplyItemFormRequest list
  FailureMessage: string option
}

type SupplyFormRequestMessage = {
  FirstAndLastName: string
  CompanyName: string
  ShippingAddress: string
  EquipmentLocation: string option
  EmailAddress: string
  BusinessPhone: string
  Items: SupplyItemFormRequest list
  FailureMessage: string option
}

type ServiceFormRequest = {
  RecaptchaResponse: string
  FirstAndLastName: string
  CompanyName: string
  ServiceAddress: string
  EmailAddress: string
  BusinessPhone: string
  EquipmentId: string
  Make: string
  Model: string
  Description: string
  FailureMessage: string option
}

type ServiceFormRequestMessage = {
  FirstAndLastName: string
  CompanyName: string
  ServiceAddress: string
  EmailAddress: string
  BusinessPhone: string
  EquipmentId: string
  Make: string
  Model: string
  Description: string
  FailureMessage: string option
}

type MeterTypeForm =
  | Mono
  | Color

  member this.toString =
    match this with
    | Mono -> "B\W"
    | Color -> "Color"

  static member fromString(s: string) =
    if s.Equals("Color", StringComparison.OrdinalIgnoreCase) then
      Color
    else
      Mono

type MeterReadingDetailFormRequest = {
  MeterType: MeterTypeForm
  Reading: int
}

type MeterReadingFormRequest = {
  RecaptchaResponse: string
  FirstAndLastName: string
  CompanyName: string
  ServiceAddress: string
  EmailAddress: string
  SendConfirmationEmail: bool
  BusinessPhone: string
  EquipmentId: string
  Make: string
  Model: string
  MeterReadings: MeterReadingDetailFormRequest list
  FailureMessage: string option
}

type MeterReadingFormRequestMessage = {
  FirstAndLastName: string
  CompanyName: string
  ServiceAddress: string
  EmailAddress: string
  SendConfirmationEmail: bool
  BusinessPhone: string
  EquipmentId: string
  Make: string
  Model: string
  MeterReadings: MeterReadingDetailFormRequest list
  FailureMessage: string option
}
